import type { NextPage, GetServerSideProps } from 'next'
import { ParsedUrlQuery } from 'querystring'
import Image from 'next/legacy/image'
import { PageTemplate, RecommendedDocuments } from '../../components'
import { Layout } from '../../components/Layout'
import {
  PageConfig,
  PageComponentProps,
  RecommendedDocumentsProps
} from '../../types/page_component_types'
import { getPagePropsBySlug } from '../../utils/get_page_props_by_slug'
import { Locale } from '../../types/shared_types'

interface BlogPageContent {
  author: string
  content: PageComponentProps[]
  excerpt: string
  image: {
    alt: string
    src: string
  }
  meta: {
    title: string
    description: string
  }
  publicationDate: string
  readingTime: string
  slug: string
  title: string
}

const BlogPage: NextPage<{
  pageContent: BlogPageContent
  pageConfig: PageConfig
  recommendations: RecommendedDocumentsProps[]
  availableLocales?: string[]
}> = ({ pageContent, pageConfig, recommendations, availableLocales }) => {
  return (
    <Layout
      config={pageConfig}
      meta={pageContent?.meta}
      slug={pageContent?.slug}
      availableLocales={availableLocales}
      blog={{
        title: pageContent?.title,
        author: pageContent?.author,
        publicationDate: pageContent?.publicationDate,
        image: pageContent?.image?.src,
      }}
    >
      <article className="mx-auto flex max-w-[70.5rem] flex-col">
        <div className="relative mx-auto mb-8 mt-0 h-[27.5rem] w-full pr-6 pl-6 md:mt-8 lg:pr-0 lg:pl-0">
          <Image
            src={pageContent?.image?.src}
            alt={pageContent?.image?.alt || ''}
            layout="fill"
            objectFit="cover"
          />
        </div>
        <h1 className="mb-6 pr-6 pl-6 lg:pr-0 lg:pl-0">{pageContent?.title}</h1>
        <span className="flex flex-col pr-6 pl-6 md:flex-row md:justify-between lg:pr-0 lg:pl-0 ">
          {pageContent?.author && (
            <span className="mb-4 md:mb-0">
              written by <span>{pageContent?.author}</span>
            </span>
          )}
          {pageContent?.publicationDate && (
            <span>{pageContent?.publicationDate}</span>
          )}
        </span>
        {<PageTemplate {...pageContent} />}
      </article>
      <RecommendedDocuments
        recommendations={recommendations}
        config={pageConfig.recommendedConfig}
      />
    </Layout>
  )
}

export const getServerSideProps: GetServerSideProps = async context => {
  return await getPagePropsBySlug(context, '/blog', {
    with_recommendations: true
  })
}

export default BlogPage
